import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Media } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { useEditMedia } from '../hooks/use-edit-media';
import {
  cropRatio,
  croppedSize,
  EMPTY_DRAFT,
  flipHorizontal,
  flipVertical,
  frameSize,
  hasEdits,
  rotateClockwise,
  selectAspect,
  toEditBody,
  uprightSize,
  type ImageEditDraft,
  type Size,
} from '../lib/image-edit';

import { ImageEditStage, type ImageEditTool } from './image-edit-stage';
import { CropTools, ResizeTools } from './image-edit-tools';

export interface MediaImageEditorProps {
  media: Media;
  src: string;
  invalidates?: ReadonlyArray<readonly unknown[]> | undefined;
  onCancel: () => void;
  onSaved: (media: Media) => void;
}

/** The image stage, tools and footer of the preview dialog while it edits; children of its grid. */
export function MediaImageEditor({
  media,
  src,
  invalidates,
  onCancel,
  onSaved,
}: MediaImageEditorProps) {
  const { t } = useTranslation(['media', 'common']);
  const edit = useEditMedia({ invalidates: invalidates ?? [] });
  const [draft, setDraft] = useState<ImageEditDraft>(EMPTY_DRAFT);
  const [tool, setTool] = useState<ImageEditTool>('crop');
  const [loaded, setLoaded] = useState<Size | null>(null);
  // Remounts the size fields when the crop they were filled from is reset.
  const [resetCount, setResetCount] = useState(0);

  const image = uprightSize(media, loaded);
  const preview = loaded ?? image;
  const frame = frameSize(image, draft.rotate);
  const source = croppedSize(frame, draft.crop);
  const output = draft.resize ?? source;

  return (
    <>
      <div className="bg-muted min-h-0 p-4 sm:p-6" data-testid="media-editor-stage">
        <ImageEditStage
          src={src}
          preview={preview}
          draft={draft}
          tool={tool}
          ratio={cropRatio(draft.aspect, frame)}
          onCropChange={(crop) => setDraft((prev) => ({ ...prev, crop, resize: null }))}
          onPreviewLoad={setLoaded}
        />
      </div>
      <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto border-t p-5 lg:border-t-0 lg:border-l">
        <Tabs value={tool} onValueChange={(value) => setTool(value as ImageEditTool)}>
          <TabsList className="w-full">
            <TabsTrigger value="crop">{t('media:editor.tabs.crop')}</TabsTrigger>
            <TabsTrigger value="resize">{t('media:editor.tabs.resize')}</TabsTrigger>
          </TabsList>
          <TabsContent value="crop" className="pt-3">
            <CropTools
              aspect={draft.aspect}
              onAspect={(aspect) => setDraft((prev) => selectAspect(prev, aspect, frame))}
              onRotate={() => setDraft(rotateClockwise)}
              onFlipHorizontal={() => setDraft(flipHorizontal)}
              onFlipVertical={() => setDraft(flipVertical)}
            />
          </TabsContent>
          <TabsContent value="resize" className="pt-3">
            <ResizeTools
              key={resetCount}
              source={source}
              draft={draft}
              onResize={(resize) => setDraft((prev) => ({ ...prev, resize }))}
            />
          </TabsContent>
        </Tabs>
        <p className="text-sm tabular-nums" data-testid="media-editor-output">
          {t('media:editor.output', { width: output.width, height: output.height })}
        </p>
        <p className="text-muted-foreground text-xs">{t('media:editor.everywhere')}</p>
      </aside>
      <div className="bg-muted/40 col-span-full flex flex-wrap items-center justify-end gap-2 border-t px-6 py-3">
        <Button
          type="button"
          variant="link"
          className="mr-auto h-auto p-0"
          disabled={!hasEdits(draft)}
          onClick={() => {
            setDraft(EMPTY_DRAFT);
            setResetCount((count) => count + 1);
          }}
        >
          {t('media:editor.reset')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="button"
          disabled={!hasEdits(draft) || edit.isPending}
          onClick={() =>
            edit.mutate({ id: media.id, body: toEditBody(draft) }, { onSuccess: onSaved })
          }
        >
          {t('common:actions.save')}
        </Button>
      </div>
    </>
  );
}
